import { useEffect, useMemo, useState } from 'react';
import type { Fixture, SquadPlayer } from '@h2h/shared';
import { Check, Search, X } from '../ui/icons';
import { cn } from '../../lib/cn';
import { teamCode } from '../../lib/teams';
import { TeamBadge } from '../TeamBadge';
import { useHaptics } from '../../hooks/useHaptics';
import { Sheet } from '../ui/Sheet';

/** Drop GKs to the bottom and otherwise keep the provider's squad order. */
function attackFirst(players: SquadPlayer[]): SquadPlayer[] {
  return [...players].sort((a, b) => {
    const ga = a.position === 'Goalkeeper' ? 1 : 0;
    const gb = b.position === 'Goalkeeper' ? 1 : 0;
    return ga - gb;
  });
}

function matches(p: SquadPlayer, q: string): boolean {
  if (!q) return true;
  return p.name.toLowerCase().includes(q) || String(p.number ?? '').includes(q);
}

/**
 * Bottom sheet to back one "anytime goalscorer" for a fixture — the optional bonus
 * market. Lists both squads (search by name/number); tapping a player selects them
 * and closes. A backed pick can be cleared. Own goals never count (the engine grades
 * purely on the provider player id appearing among the genuine scorers).
 */
export function ScorerPickerSheet({
  open,
  fixture,
  homePlayers,
  awayPlayers,
  selectedId,
  flag = false,
  tone = 'light',
  onClose,
  onSelect
}: {
  open: boolean;
  fixture: Fixture;
  homePlayers: SquadPlayer[];
  awayPlayers: SquadPlayer[];
  selectedId?: string;
  /** National-team flags fill their tile (no white crest plate). */
  flag?: boolean;
  /** Match the parent screen — `dark` on the immersive Make Your Picks flow. */
  tone?: 'light' | 'dark';
  onClose: () => void;
  onSelect: (player: { id: string; name: string } | null) => void;
}) {
  const { impact } = useHaptics();
  const [q, setQ] = useState('');
  const dark = tone === 'dark';

  useEffect(() => {
    if (open) setQ('');
  }, [open]);

  const query = q.trim().toLowerCase();
  const sections = useMemo(
    () =>
      [
        { side: 'HOME' as const, team: fixture.homeTeam, code: teamCode(fixture.homeTeam, fixture.homeCode), logo: fixture.homeLogo, players: attackFirst(homePlayers) },
        { side: 'AWAY' as const, team: fixture.awayTeam, code: teamCode(fixture.awayTeam, fixture.awayCode), logo: fixture.awayLogo, players: attackFirst(awayPlayers) }
      ].map((s) => ({ ...s, players: s.players.filter((p) => matches(p, query)) })),
    [fixture, homePlayers, awayPlayers, query]
  );

  const empty = sections.every((s) => s.players.length === 0);

  const choose = (p: SquadPlayer) => {
    impact('selection');
    onSelect({ id: p.id, name: p.name });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      ariaLabel="Pick a goalscorer"
      tone={tone}
      className="flex max-h-[84vh] flex-col"
    >
      <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-2">
        <div className="min-w-0">
          <h2 className={cn('text-[20px] font-extrabold tracking-[-0.02em]', dark ? 'text-white' : 'text-ink')}>
            Pick a goalscorer
          </h2>
          <p className={cn('mt-0.5 text-[12px] font-semibold leading-[1.35]', dark ? 'text-white/55' : 'text-ink2')}>
            Back anyone to score at any time in {fixture.homeTeam} v {fixture.awayTeam}.
          </p>
        </div>
        {selectedId && (
          <button
            type="button"
            onClick={() => {
              impact('selection');
              onSelect(null);
            }}
            className={cn(
              'tap shrink-0 self-center rounded-[10px] px-3 py-2 text-[12px] font-bold',
              dark ? 'bg-white/[0.10] text-white/70' : 'bg-fill text-ink2'
            )}
          >
            Clear
          </button>
        )}
      </div>

      {/* search */}
      <div className="px-4 pb-2">
        <div
          className={cn(
            'flex items-center gap-2 rounded-[14px] border px-3 py-2.5',
            dark ? 'border-white/[0.10] bg-white/[0.08]' : 'border-black/[0.08] bg-white'
          )}
        >
          <Search width={16} height={16} strokeWidth={2.4} className={cn('shrink-0', dark ? 'text-white/45' : 'text-ink3')} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search players"
            className={cn(
              'min-w-0 flex-1 bg-transparent text-[14px] font-medium outline-none',
              dark ? 'text-white placeholder:text-white/40' : 'text-ink placeholder:text-ink3'
            )}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className={cn('tap shrink-0', dark ? 'text-white/45' : 'text-ink3')}
              aria-label="Clear search"
            >
              <X width={16} height={16} strokeWidth={2.6} />
            </button>
          )}
        </div>
      </div>

      <div className="native-scroll no-scrollbar flex-1 overflow-y-auto px-4">
        {empty ? (
          <p className={cn('py-10 text-center text-[13px] font-semibold', dark ? 'text-white/55' : 'text-ink2')}>
            No players match “{q}”.
          </p>
        ) : (
          <div className="flex flex-col gap-4 pb-3">
            {sections.map((s) =>
              s.players.length === 0 ? null : (
                <div key={s.side}>
                  <div className="mb-2 flex items-center gap-2 px-0.5">
                    <TeamBadge
                      code={s.code}
                      logo={s.logo}
                      name={s.team}
                      flag={flag}
                      sizeClass="h-[22px] w-[22px]"
                      roundedClass="rounded-[7px]"
                      textClass="text-[8px]"
                      fallback="dark"
                      className={flag ? 'ring-1 ring-inset ring-white/15' : undefined}
                    />
                    <span
                      className={cn(
                        'text-[12px] font-extrabold uppercase tracking-[0.05em]',
                        dark ? 'text-white/50' : 'text-ink2'
                      )}
                    >
                      {s.team}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {s.players.map((p) => {
                      const checked = p.id === selectedId;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => choose(p)}
                          className={cn(
                            'tap flex items-center gap-3 rounded-[14px] border px-3 py-2.5 text-left transition-colors active:scale-[0.99]',
                            checked
                              ? dark
                                ? 'border-accent/40 bg-accent/[0.18]'
                                : 'border-[1.5px] border-accent bg-white'
                              : dark
                                ? 'border-white/[0.08] bg-white/[0.05]'
                                : 'border-black/[0.08] bg-white'
                          )}
                        >
                          <span
                            className={cn(
                              'num grid h-[28px] w-[28px] shrink-0 place-items-center rounded-[9px] text-[12px] font-extrabold',
                              dark ? 'bg-white/[0.10] text-white/55' : 'bg-fill text-ink2'
                            )}
                          >
                            {p.number ?? '–'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div
                              className={cn(
                                'truncate text-[14px] font-bold tracking-[-0.01em]',
                                dark ? 'text-white' : 'text-ink'
                              )}
                            >
                              {p.name}
                            </div>
                            {p.position && (
                              <div className={cn('text-[11px] font-medium', dark ? 'text-white/50' : 'text-ink3')}>
                                {p.position}
                              </div>
                            )}
                          </div>
                          <span
                            className={cn(
                              'grid h-[24px] w-[24px] shrink-0 place-items-center rounded-full border-2',
                              checked ? 'border-accent bg-accent' : dark ? 'border-white/25' : 'border-ink4'
                            )}
                          >
                            {checked && <Check width={13} height={13} strokeWidth={3.5} className="text-[#06371F]" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}
